import { useMe } from '@servicetim/ui/api';
import { hasTenantRole, useSession, useSessionClaims } from '@servicetim/ui/lib';
import { ShieldAlert } from 'lucide-react';

/**
 * Bolt 10: shown to admins/owners on `/tenant` when the deploy enforces `REQUIRE_ADMIN_MFA`
 * (`GET /admin/v1/me` -> `requireAdminMfa`, `apps/api/src/admin/me.ts`). Cognito Managed
 * Login has no self-service "/mfa" or "/account" page (verified — it only prompts a user to
 * enroll during sign-in, and only when the user pool's own MFA setting is `Required`), so the
 * call to action re-runs this app's normal sign-in redirect rather than linking to a page that
 * does not exist; the note tells the admin what to expect instead.
 */
export function AdminMfaBanner() {
  const { tenantRole } = useSessionClaims();
  const { signIn, authEnabled } = useSession();
  const me = useMe();

  if (!authEnabled || !hasTenantRole(tenantRole, 'admin') || me.data?.requireAdminMfa !== true) {
    return null;
  }

  return (
    <div
      role="status"
      className="mb-4 flex items-start gap-3 rounded-md border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/15 px-4 py-3 text-amber-900 dark:text-amber-300 text-sm"
    >
      <ShieldAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <div className="flex flex-col gap-1">
        <p>
          <strong className="font-semibold">
            Multi-factor authentication is required for admins.
          </strong>{' '}
          If you have not enrolled an MFA method yet, sign in again and follow the prompt on the
          sign-in page to set one up — Cognito Managed Login does not have a separate account page
          for this.
        </p>
        <button
          type="button"
          className="w-fit font-medium underline underline-offset-2 hover:no-underline"
          onClick={() => void signIn()}
        >
          Sign in again to set up MFA
        </button>
      </div>
    </div>
  );
}
